import React, { useEffect, useRef, useState } from "react";
import { api, errMsg, streamTutor, GRADE_BANDS } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SendHorizonal, Sparkles, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { v: "guide", label: "Guide me step by step" },
  { v: "explain", label: "Explain the idea" },
  { v: "practice", label: "Give me practice" },
];

const STARTERS = {
  lesson: ["Can you explain this lesson in simpler words?", "Quiz me on this lesson", "I don't understand the vocabulary"],
  free: ["Help me with a math problem", "Explain photosynthesis", "Help me plan an essay"],
};

// Strip markdown so text-to-speech sounds natural
const speakable = (md) =>
  (md || "")
    .replace(/`{1,3}[^`]*`{1,3}/g, " ")
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, "$1")
    .replace(/\[([^\]]+)]\([^)]+\)/g, "$1")
    .replace(/[#>_~`]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const SpeechRec = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const voiceSupported = Boolean(SpeechRec);
const ttsSupported = typeof window !== "undefined" && !!window.speechSynthesis;

export const TutorChat = ({ sessionId, compact = false, lessonMode = false, onTitleChange }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("guide");
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [grade, setGrade] = useState(null);
  const endRef = useRef(null);
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [speakOn, setSpeakOn] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("sc_tutor_speak") === "1";
  });
  const lastSpokenRef = useRef("");

  // --- Speech-to-text (built-in browser Web Speech API) ---
  const toggleListen = () => {
    if (!voiceSupported) {
      toast.error("Voice mode isn't supported in this browser. Try Chrome, Edge or Safari.");
      return;
    }
    if (listening) {
      try { recRef.current?.stop(); } catch (_) {}
      return;
    }
    try {
      const rec = new SpeechRec();
      rec.lang = navigator.language || "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      let finalText = "";
      rec.onresult = (ev) => {
        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const t = ev.results[i][0].transcript;
          if (ev.results[i].isFinal) finalText += t;
          else interim += t;
        }
        setInput((finalText + interim).trim());
      };
      rec.onerror = (e) => {
        setListening(false);
        if (e.error === "not-allowed") toast.error("Microphone access was blocked. Allow it in your browser settings.");
        else if (e.error !== "aborted" && e.error !== "no-speech") toast.error(`Voice error: ${e.error}`);
      };
      rec.onend = () => {
        setListening(false);
        if (finalText.trim()) {
          // auto-send after a short pause so kids don't have to hunt for the button
          setTimeout(() => send(finalText.trim()), 150);
        }
      };
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      toast.error("Couldn't start voice. " + (err.message || ""));
    }
  };

  // --- Text-to-speech for Sage's replies ---
  const toggleSpeak = () => {
    if (!ttsSupported) {
      toast.error("Spoken replies aren't supported in this browser.");
      return;
    }
    const next = !speakOn;
    setSpeakOn(next);
    localStorage.setItem("sc_tutor_speak", next ? "1" : "0");
    if (!next) window.speechSynthesis.cancel();
  };

  const sageSays = (text) => {
    if (!speakOn || !ttsSupported) return;
    const words = speakable(text);
    if (!words || words === lastSpokenRef.current) return;
    lastSpokenRef.current = words;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(words);
      u.rate = 0.95;
      u.pitch = 1.0;
      // Prefer a friendly voice if the OS has one
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find((v) => /female|samantha|zira|google.*english/i.test(v.name)) || voices.find((v) => (v.lang || "").startsWith("en"));
      if (preferred) u.voice = preferred;
      window.speechSynthesis.speak(u);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => () => {
    try { recRef.current?.abort(); } catch (_) {}
    if (ttsSupported) window.speechSynthesis.cancel();
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    api
      .get(`/tutor/sessions/${sessionId}`)
      .then(({ data }) => {
        setMessages(data.messages || []);
        setGrade(data.grade_band);
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, draft]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content, message_id: `local-${Date.now()}` }]);
    setStreaming(true);
    setDraft("");
    try {
      const full = await streamTutor(sessionId, content, mode, setDraft);
      setMessages((m) => [...m, { role: "assistant", content: full, message_id: `local-a-${Date.now()}` }]);
      sageSays(full);
      if (messages.length === 0 && onTitleChange) onTitleChange(content.slice(0, 60));
    } catch (e) {
      toast.error(e.message || "Tutor is unavailable");
    } finally {
      setDraft("");
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-card" data-testid="tutor-panel">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border bg-[#FBFAF4]">
        <div className="flex items-center gap-2 mr-auto">
          <div className="h-8 w-8 rounded-lg bg-[#D7F3F1] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-[#0B6B6B]" />
          </div>
          <div>
            <div className="font-display font-semibold text-sm leading-tight">Sage, your AI tutor</div>
            {grade && <div className="text-[11px] text-muted-foreground">Talking at {grade} level</div>}
          </div>
        </div>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="h-9 w-[190px] bg-white text-xs" data-testid="tutor-mode-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODES.map((m) => (
              <SelectItem key={m.v} value={m.v} data-testid={`tutor-mode-${m.v}`}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {ttsSupported && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-9 w-9 ${speakOn ? "text-[#0F7B7D] bg-[#D7F3F1]" : "text-muted-foreground"}`}
            onClick={toggleSpeak}
            aria-label={speakOn ? "Mute Sage" : "Hear Sage speak"}
            title={speakOn ? "Sage will speak replies" : "Sage will stay quiet"}
            data-testid="tutor-speak-toggle"
          >
            {speakOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
        )}
      </div>

      <div className={`flex-1 overflow-y-auto px-4 py-4 space-y-3 ${compact ? "min-h-[280px]" : "min-h-[360px]"}`} data-testid="tutor-message-list" aria-live="polite">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading conversation...</div>
        ) : messages.length === 0 && !streaming ? (
          <div className="py-6">
            <p className="text-sm text-muted-foreground mb-4">
              Ask anything. Sage won't just hand you answers — it'll guide you to figure things out yourself.
            </p>
            <div className="flex flex-col gap-2">
              {(lessonMode ? STARTERS.lesson : STARTERS.free).map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left text-sm px-3 py-2.5 rounded-lg border border-border bg-white hover:bg-[#D7F3F1] transition-colors duration-150"
                  data-testid="tutor-starter-prompt"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.message_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} fade-up`}>
              <div
                className={`max-w-[88%] rounded-[14px] px-3.5 py-2.5 text-sm ${
                  m.role === "user" ? "bg-[#E9E1CF] text-[#071A22]" : "bg-[#D7F3F1] text-[#071A22]"
                }`}
                data-testid={m.role === "user" ? "tutor-user-message" : "tutor-assistant-message"}
              >
                {m.role === "user" ? <div className="whitespace-pre-wrap">{m.content}</div> : <Markdown>{m.content}</Markdown>}
              </div>
            </div>
          ))
        )}
        {streaming && (
          <div className="flex justify-start">
            <div className="max-w-[88%] rounded-[14px] px-3.5 py-2.5 text-sm bg-[#D7F3F1]" data-testid="tutor-streaming-message">
              {draft ? <Markdown>{draft}</Markdown> : <span className="typing"><span /><span /><span /></span>}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-border p-3 bg-white">
        <div className="flex gap-2 items-end">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={compact ? 2 : 2}
            placeholder={listening ? "Listening… speak now" : "Type your question or tap the mic (Enter to send)"}
            className={`resize-none bg-[#FBFAF4] min-h-[52px] ${listening ? "ring-2 ring-[#0F7B7D]" : ""}`}
            data-testid="tutor-input"
          />
          {voiceSupported && (
            <Button
              onClick={toggleListen}
              disabled={streaming}
              variant={listening ? "default" : "outline"}
              className={`h-[52px] w-[52px] p-0 shrink-0 ${listening ? "bg-[#0F7B7D] hover:bg-[#0B6B6B] text-white animate-pulse" : ""}`}
              aria-label={listening ? "Stop listening" : "Start voice input"}
              title={listening ? "Tap to stop and send" : "Tap and speak"}
              data-testid="tutor-mic-button"
            >
              {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>
          )}
          <Button onClick={() => send()} disabled={streaming || !input.trim()} className="h-[52px] px-4" data-testid="tutor-send-button" aria-label="Send">
            <SendHorizonal className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export { GRADE_BANDS };
